// @flow
import moment from 'moment';

import { constants } from '@clubmed/utils';

import { type ButtonProps } from 'Components/frameworks/button/Button.jsx';
import { ICONS } from 'Components/utils/icon/icons';
import { COLORS } from 'Components/utils/color/colors';
import {
  type PriceTicketInfo,
  type PriceTicketItem,
} from 'Components/widgets/price-ticket/PriceTicketContentWrapper';
import { QUOTATION_FORM_SECTION_ID } from 'Components/widgets/product-quotation-form/productQuotationForm.constants';
import { t } from 'Src/i18n';

const { PRODUCT_TYPES } = constants;

export function getPriceTicketButton(product: Resort): ButtonProps {
  const props = {
    children: t('productPriceTicketLine.seeAllPrices'),
    colorBg: COLORS.BOOKING,
    colorBorder: COLORS.BOOKING,
    iconLeft: { name: ICONS.DATE },
    url: '#booking',
    trackingData: {
      actionGA: 'priceBar',
      labelGA: 'checkPrices',
    },
  };

  switch (product.type) {
    case PRODUCT_TYPES.TOUR:
      return {
        ...props,
        iconLeft: {},
        children: t('productPriceTicketLine.askForQuotation'),
        url: `#${QUOTATION_FORM_SECTION_ID}`,
      };

    case PRODUCT_TYPES.CRUISE:
      return {
        ...props,
        iconLeft: {},
        children: t('productPriceTicketLine.book'),
      };

    default:
      return props;
  }
}

export function getPriceTicketSubtitle(product: Resort, cruise?: null | Cruise): string {
  const { destination = {}, type = '' } = product;

  switch (type) {
    case PRODUCT_TYPES.CRUISE:
      return cruise?.fromToDestination || '';
    default:
      return destination.country || '';
  }
}

export function getPriceTicketInfo({ type }: Resort): PriceTicketInfo {
  switch (type) {
    case PRODUCT_TYPES.TOUR:
      return {};
    case PRODUCT_TYPES.JOYVIEW:
      return {
        iconName: ICONS.ACCOMODATIONS,
        label: t('resort.startingPrice.roomOnly'),
      };
    default:
      return {
        iconName: ICONS.ALL_INCLUSIVE,
        label: t('resort.startingPrice.allInclusive'),
      };
  }
}

function getPriceTicketItem({
  iconName,
  description,
  label = '',
}: PriceTicketItem): PriceTicketItem | null {
  return description ? { description, iconName, label } : null;
}

export function getPriceTicketItems({
  product,
  price,
}: {
  product: Resort,
  price: ProductPrice,
}): Array<PriceTicketItem> {
  const { type, duration = '' } = product;

  /* departureDate */
  const departureDateValue = price.termsAndConditions?.departureDate || 0;
  const departureDate =
    type !== PRODUCT_TYPES.TOUR
      ? getPriceTicketItem({
          iconName: ICONS.DATE,
          description: moment(departureDateValue, 'YYYYMMDD').format(
            t('productPriceTicketLine.departureDateFormat'),
          ),
          label: t('productPriceTicketLine.departureDateLabel'),
        })
      : null;

  /* duration */
  const durationTour =
    type === PRODUCT_TYPES.TOUR
      ? getPriceTicketItem({ iconName: ICONS.DURATION, description: duration })
      : null;

  /* transport */
  const departureCityName = price.termsAndConditions?.departureCity?.name;

  const transport = departureCityName
    ? getPriceTicketItem({
        iconName: ICONS.TRANSPORT,
        description: departureCityName,
        label: t('productPriceTicketLine.departureCityLabel'),
      })
    : getPriceTicketItem({
        iconName: ICONS.NO_TRANSPORT,
        description: t('booking.form.withoutTransport'),
      });

  /* accommodations */
  const priceDurationFormatted = price.termsAndConditions?.durationFormatted;
  const displayedDuration = type === PRODUCT_TYPES.CRUISE ? duration : priceDurationFormatted;

  const accommodations =
    type !== PRODUCT_TYPES.TOUR
      ? getPriceTicketItem({
          iconName: ICONS.ACCOMODATIONS,
          description: price.termsAndConditions?.accommodationComfortLabel,
          label: displayedDuration,
        })
      : null;

  /* persons */
  const numberOfAdults = Number(price.termsAndConditions?.numberOfAdults || 0);
  const personLabel =
    numberOfAdults > 1 ? t('productPriceTicketLine.adults') : t('productPriceTicketLine.adult');

  const persons = getPriceTicketItem({
    iconName: ICONS.USERS,
    description: personLabel,
    label: numberOfAdults.toString(),
  });

  return [departureDate, durationTour, transport, accommodations, persons].filter(Boolean);
}
